import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Search, FileText, Sparkles, Target, User, Loader2, MapPin,
  Briefcase, GraduationCap, Mail, Phone, Eye, X, Trophy
} from "lucide-react";

const TABS = [
  { id: "match", label: "CV × Commessa", icon: Target, color: "#10B981" },
  { id: "library", label: "Libreria CV", icon: FileText, color: "#3B82F6" },
  { id: "search", label: "Ricerca AI", icon: Sparkles, color: "#7C3AED" },
];

export default function AdminCVPage() {
  const params = new URLSearchParams(window.location.search);
  const initialTab = params.get("tab");
  const [tab, setTab] = useState(TABS.some(t => t.id === initialTab) ? initialTab : "match");
  return (
    <div className="p-5 md:p-8 max-w-[1280px] mx-auto" data-testid="admin-cv-page">
      <div className="mb-6">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-blue-600 mb-2">— HR Recruiting</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Curriculum Vitae</h1>
        <p className="mt-2 text-slate-600 max-w-[620px]">Gestisci i CV dei dipendenti, cerca con linguaggio naturale via AI, confronta offerte di lavoro con i profili disponibili.</p>
      </div>

      <div className="grid grid-cols-12 gap-5 md:gap-8">
        <aside className="col-span-12 md:col-span-3">
          <div className="md:sticky md:top-6 rounded-2xl p-3 bg-white border border-slate-200">
            <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold px-3 pt-2 pb-3 text-slate-400">Sezioni</div>
            <nav className="space-y-1" data-testid="cv-tabs">
              {TABS.map(t => {
                const Icon = t.icon; const active = t.id === tab;
                return (
                  <button key={t.id} onClick={()=>setTab(t.id)} data-testid={`cv-tab-${t.id}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all text-left"
                    style={{
                      background: active ? `linear-gradient(135deg, ${t.color}18 0%, ${t.color}08 100%)` : "transparent",
                      color: active ? "#0F172A" : "#475569",
                      border: active ? `1px solid ${t.color}40` : "1px solid transparent",
                    }}>
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: active ? `linear-gradient(135deg, ${t.color} 0%, ${t.color}CC 100%)` : `${t.color}15` }}>
                      <Icon className="w-4 h-4" style={{ color: active ? "#FFFFFF" : t.color }} />
                    </span>
                    <span className="flex-1">{t.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        <div className="col-span-12 md:col-span-9" data-testid={`cv-panel-${tab}`}>
          {tab === "library" && <LibraryPanel/>}
          {tab === "search" && <SearchPanel/>}
          {tab === "match" && <MatchPanel/>}
        </div>
      </div>
    </div>
  );
}

// -------------- LIBRARY ---------------
function LibraryPanel() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const load = async (query = "") => {
    setLoading(true);
    try {
      const r = await api.get("/admin/cvs", { params: query ? { q: query } : {} });
      setItems(r.data.items || []);
    } catch (e) { toast.error("Errore caricamento CV"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="rounded-2xl p-4 bg-white border border-slate-200 mb-4 flex gap-2">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-400"/>
          <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')load(q);}}
            placeholder="Cerca per nome, skill, ruolo, città, istituto…" data-testid="cv-library-search"
            className="w-full pl-9 pr-3 h-11 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-blue-400"/>
        </div>
        <Button onClick={()=>load(q)} data-testid="cv-library-search-btn" className="rounded-xl bg-blue-500 hover:bg-blue-600">Cerca</Button>
      </div>

      <div className="text-xs text-slate-500 mb-3">{loading ? "Caricamento…" : `${items.length} CV trovati`}</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cv-library-grid">
        {items.map(it => <CVCard key={it.id} cv={it} onOpen={()=>setOpen(it)}/>)}
        {!loading && items.length === 0 && <div className="col-span-full text-center text-slate-500 py-12">Nessun CV ancora caricato. I dipendenti possono caricare il proprio CV dal portale.</div>}
      </div>

      {open && <CVModal cv={open} onClose={()=>setOpen(null)}/>}
    </div>
  );
}

function CVCard({ cv, onOpen, score, reason, strengths, gaps }) {
  const p = cv.personal || cv.cv?.personal || {};
  const emp = cv._employee || cv.employee;
  const skills = cv.skills || cv.cv?.skills || [];
  const scoreColor = score == null ? null : score >= 75 ? "#10B981" : score >= 50 ? "#F59E0B" : "#64748B";
  return (
    <div className="rounded-2xl p-4 bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
      onClick={onOpen} data-testid={`cv-card-${cv.id || cv.cv?.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
          {(p.full_name || emp?.full_name || "?").split(" ").map(s=>s[0]).slice(0,2).join("")}
        </div>
        {score != null && (
          <div className="px-2 py-1 rounded-full text-xs font-bold" style={{ background: `${scoreColor}20`, color: scoreColor }}>
            {score}%
          </div>
        )}
      </div>
      <div className="font-bold text-sm">{p.full_name || emp?.full_name || "Senza nome"}</div>
      <div className="text-xs text-slate-500 mt-0.5">{emp?.job_role || p.city || "—"}</div>
      {reason && <div className="mt-2 text-xs text-slate-600 italic">"{reason}"</div>}
      {strengths?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {strengths.slice(0, 3).map((s, i) => <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">+ {s}</span>)}
        </div>
      )}
      {gaps?.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {gaps.slice(0, 2).map((g, i) => <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-700">− {g}</span>)}
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-1">
        {skills.slice(0, 4).map((s, i) => <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700">{s}</span>)}
        {skills.length > 4 && <span className="px-2 py-0.5 rounded-full text-[10px] text-slate-400">+{skills.length - 4}</span>}
      </div>
    </div>
  );
}

function CVModal({ cv: raw, onClose }) {
  const cv = raw.cv || raw;
  const p = cv.personal || {};
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.60)", backdropFilter: "blur(6px)" }} data-testid="cv-modal">
      <div className="bg-white rounded-2xl max-w-[720px] w-full max-h-[90vh] overflow-y-auto p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid="cv-modal-close"><X className="w-4 h-4"/></button>
        <div className="flex items-start gap-4 mb-5">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
            {(p.full_name || "?").split(" ").map(s=>s[0]).slice(0,2).join("")}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-extrabold">{p.full_name || "Senza nome"}</h2>
            <div className="text-sm text-slate-600 mt-1 flex flex-wrap gap-x-4">
              {p.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3"/> {p.email}</span>}
              {p.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/> {p.phone}</span>}
              {p.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/> {p.city}</span>}
            </div>
          </div>
        </div>
        {cv.summary && <div className="mb-5 p-3 rounded-xl bg-indigo-50 text-sm text-slate-800">{cv.summary}</div>}
        {cv.experience?.length > 0 && (
          <ModalSection icon={Briefcase} title="Esperienza">
            {cv.experience.map((e, i) => (
              <div key={i} className="pl-3 border-l-2 border-blue-200 mb-3">
                <div className="font-bold text-sm">{e.role} <span className="font-normal text-slate-500">@ {e.company}</span></div>
                <div className="text-xs text-slate-500">{e.from} → {e.to || "presente"}</div>
                {e.description && <div className="text-xs text-slate-700 mt-1">{e.description}</div>}
              </div>
            ))}
          </ModalSection>
        )}
        {cv.education?.length > 0 && (
          <ModalSection icon={GraduationCap} title="Formazione">
            {cv.education.map((e, i) => (
              <div key={i} className="pl-3 border-l-2 border-purple-200 mb-2">
                <div className="font-bold text-sm">{e.degree} {e.field && <span className="font-normal text-slate-500">· {e.field}</span>}</div>
                <div className="text-xs text-slate-500">{e.institution} · {e.from}–{e.to}</div>
              </div>
            ))}
          </ModalSection>
        )}
        {cv.skills?.length > 0 && (
          <ModalSection icon={Sparkles} title="Skills">
            <div className="flex flex-wrap gap-1.5">{cv.skills.map((s, i) => <span key={i} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">{s}</span>)}</div>
          </ModalSection>
        )}
        {cv.languages?.length > 0 && (
          <ModalSection icon={User} title="Lingue">
            <div className="flex flex-wrap gap-1.5">{cv.languages.map((l, i) => <span key={i} className="px-2.5 py-1 rounded-full text-xs bg-emerald-100 text-emerald-800">{l.name} <strong>{l.level}</strong></span>)}</div>
          </ModalSection>
        )}
      </div>
    </div>
  );
}

function ModalSection({ icon: Icon, title, children }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4 text-slate-500"/><h3 className="font-bold text-sm uppercase tracking-wide text-slate-700">{title}</h3></div>
      {children}
    </div>
  );
}

// -------------- AI SEARCH ---------------
function SearchPanel() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const run = async () => {
    if (q.trim().length < 4) { toast.error("Query troppo breve"); return; }
    setLoading(true); setResults([]);
    try {
      const r = await api.post("/admin/cv-search-ai", { query: q, top_k: 10 });
      setResults(r.data.items || []);
      if (!r.data.items?.length) toast.info("Nessun CV corrispondente trovato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore AI"); }
    finally { setLoading(false); }
  };
  return (
    <div>
      <div className="rounded-2xl p-6 bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-5 h-5 text-violet-500"/>
          <h3 className="font-bold text-lg">Cerca con AI GPT-5</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">Descrivi il profilo che cerchi in linguaggio naturale. Esempio: <em>"sviluppatore Python con esperienza 5+ anni che parli inglese"</em>.</p>
        <div className="flex gap-2">
          <textarea value={q} onChange={e=>setQ(e.target.value)} data-testid="cv-ai-search-input" rows={2}
            placeholder="Descrivi il candidato ideale…"
            className="flex-1 px-4 py-3 rounded-xl border border-violet-200 text-sm resize-none bg-white focus:outline-none focus:border-violet-400"/>
          <Button onClick={run} disabled={loading} data-testid="cv-ai-search-btn"
            className="rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 text-white px-6 self-stretch">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Sparkles className="w-4 h-4 mr-1"/> Trova</>}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cv-ai-search-results">
        {results.map(r => <CVCard key={r.cv.id} cv={r.cv} onOpen={()=>setOpen(r)} score={r.score} reason={r.reason}/>)}
      </div>

      {open && <CVModal cv={open} onClose={()=>setOpen(null)}/>}
    </div>
  );
}

// -------------- JOB MATCH ---------------
function MatchPanel() {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const run = async () => {
    if (!title.trim() || desc.trim().length < 20) { toast.error("Compila titolo e descrizione (min 20 caratteri)"); return; }
    setLoading(true); setResults([]);
    try {
      const r = await api.post("/admin/cv-match-job", { job_title: title, job_description: desc, top_k: 15 });
      setResults(r.data.items || []);
      if (!r.data.items?.length) toast.info("Nessun candidato con congruenza ≥ 30%");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore AI"); }
    finally { setLoading(false); }
  };
  const podium = results.slice(0, 3);
  const rest = results.slice(3);
  return (
    <div>
      {/* INPUT COMMESSA */}
      <div className="rounded-2xl p-6 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-5 h-5 text-emerald-600"/>
          <h3 className="font-bold text-lg">CV × Commessa · analisi di associamento</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">Incolla la commessa / offerta di lavoro: GPT-5 incrocia la libreria CV e calcola in 3 secondi la classifica con congruenza %, punti di forza, lacune e il <strong>reasoning</strong> dell'AI per ogni candidato.</p>
        <div className="space-y-3">
          <input value={title} onChange={e=>setTitle(e.target.value)} data-testid="cv-match-title"
            placeholder="Titolo commessa (es. Senior Project Manager edilizia, Sviluppatore Python, …)"
            className="w-full px-4 h-11 rounded-xl border border-emerald-200 bg-white text-sm focus:outline-none focus:border-emerald-400"/>
          <textarea value={desc} onChange={e=>setDesc(e.target.value)} data-testid="cv-match-desc" rows={6}
            placeholder="Incolla qui tutta la commessa: contesto, responsabilità, requisiti tecnici, soft-skills, esperienza richiesta, lingue, sede, contratto…"
            className="w-full px-4 py-3 rounded-xl border border-emerald-200 bg-white text-sm resize-none focus:outline-none focus:border-emerald-400"/>
          <Button onClick={run} disabled={loading} data-testid="cv-match-btn"
            className="w-full h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/> L'AI sta analizzando i profili…</> : <><Target className="w-4 h-4 mr-2"/> Calcola la classifica dei candidati</>}
          </Button>
        </div>
      </div>

      {/* PODIO TOP 3 */}
      {podium.length > 0 && (
        <div data-testid="cv-match-podium" className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-amber-500"/>
            <h3 className="font-bold text-sm uppercase tracking-wide text-slate-700">Top {podium.length} per la commessa "{title}"</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {podium.map((r, i) => <PodiumCard key={r.cv.id} rank={i+1} match={r} onOpen={()=>setOpen(r)}/>)}
          </div>
        </div>
      )}

      {/* RESTO CLASSIFICA */}
      {rest.length > 0 && (
        <div data-testid="cv-match-rest">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">Altri candidati pertinenti</div>
          <div className="space-y-2">
            {rest.map((r, i) => <RankedRow key={r.cv.id} rank={i+4} match={r} onOpen={()=>setOpen(r)}/>)}
          </div>
        </div>
      )}

      {open && <CVModal cv={open} onClose={()=>setOpen(null)}/>}
    </div>
  );
}

function scoreColor(score) {
  return score >= 80 ? "#10B981" : score >= 60 ? "#3B82F6" : score >= 40 ? "#F59E0B" : "#64748B";
}

function PodiumCard({ rank, match, onOpen }) {
  const cv = match.cv;
  const p = cv.personal || cv.cv?.personal || {};
  const emp = match.employee;
  const color = scoreColor(match.score);
  const medal = rank === 1 ? "🥇" : rank === 2 ? "🥈" : "🥉";
  return (
    <div onClick={onOpen} data-testid={`cv-match-podium-${rank}`}
      className="rounded-2xl p-5 bg-white border-2 hover:shadow-xl transition-all cursor-pointer relative overflow-hidden"
      style={{ borderColor: `${color}50` }}>
      <div className="absolute top-0 right-0 w-32 h-32 opacity-10" style={{ background: `radial-gradient(circle at top right, ${color}, transparent 70%)` }}/>
      <div className="flex items-start justify-between mb-3 relative">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{medal}</span>
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">#{rank}</span>
        </div>
        <div className="text-right">
          <div className="font-bold text-3xl" style={{ color }}>{match.score}%</div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Congruenza</div>
        </div>
      </div>
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm mb-3">
        {(p.full_name || emp?.full_name || "?").split(" ").map(s=>s[0]).slice(0,2).join("")}
      </div>
      <div className="font-bold text-base">{p.full_name || emp?.full_name || "Senza nome"}</div>
      <div className="text-xs text-slate-500 mt-0.5 mb-3">{emp?.job_role || p.city || "—"}</div>

      {match.reasoning && (
        <div className="mb-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
          <div className="flex items-center gap-1 mb-1">
            <Sparkles className="w-3 h-3 text-violet-500"/>
            <span className="text-[9px] font-bold uppercase tracking-widest text-violet-600">AI Reasoning</span>
          </div>
          <div className="text-xs text-slate-700 leading-snug">{match.reasoning}</div>
        </div>
      )}

      {match.strengths?.length > 0 && (
        <div className="mb-2">
          <div className="text-[9px] font-bold uppercase tracking-widest text-emerald-600 mb-1">Punti di forza</div>
          <div className="flex flex-wrap gap-1">
            {match.strengths.slice(0, 4).map((s, i) => <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">+ {s}</span>)}
          </div>
        </div>
      )}
      {match.gaps?.length > 0 && (
        <div>
          <div className="text-[9px] font-bold uppercase tracking-widest text-rose-600 mb-1">Lacune</div>
          <div className="flex flex-wrap gap-1">
            {match.gaps.slice(0, 3).map((g, i) => <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-700">− {g}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

function RankedRow({ rank, match, onOpen }) {
  const cv = match.cv;
  const p = cv.personal || cv.cv?.personal || {};
  const emp = match.employee;
  const color = scoreColor(match.score);
  return (
    <div onClick={onOpen} data-testid={`cv-match-row-${rank}`}
      className="rounded-xl p-3 bg-white border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer flex items-center gap-3">
      <div className="text-[11px] font-bold text-slate-400 w-6 text-center">#{rank}</div>
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs shrink-0">
        {(p.full_name || emp?.full_name || "?").split(" ").map(s=>s[0]).slice(0,2).join("")}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">{p.full_name || emp?.full_name || "Senza nome"}</div>
        <div className="text-xs text-slate-500 truncate">{match.reasoning || emp?.job_role || p.city || "—"}</div>
      </div>
      <div className="hidden sm:flex flex-wrap gap-1 max-w-[220px] justify-end">
        {match.strengths?.slice(0, 2).map((s, i) => <span key={i} className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-100 text-emerald-700 whitespace-nowrap">+ {s}</span>)}
      </div>
      <div className="text-right shrink-0 w-16">
        <div className="font-bold text-lg" style={{ color }}>{match.score}%</div>
      </div>
    </div>
  );
}
